/**
 * useVacations hook
 * Orchestrates: data loading (vacations, employees, holidays, schulferien),
 * drag selection, submit/delete, and navigation.
 */
import { useState, useEffect, useCallback } from 'react';
import { format, addDays, subDays, addMonths, subMonths, addWeeks, subWeeks } from 'date-fns';
import { toast } from 'sonner';
import { vacationsAPI, employeesAPI, feiertageAPI, schulferienAPI } from '../../lib/api';

export function useVacations({ canManageOthers }) {
  const [vacations, setVacations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRange, setSelectedRange] = useState({ from: null, to: null });
  const [formData, setFormData] = useState({
    vacation_type: 'Urlaub',
    notes: '',
    employee_id: '',
  });
  const [preview, setPreview] = useState(null);

  // Drag selection
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  // Holidays
  const [holidays, setHolidays] = useState([]);
  const [companyFreeDays, setCompanyFreeDays] = useState([]);
  const [schulferien, setSchulferien] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const [vacRes, empRes, holRes, freeDaysRes, ferienRes] = await Promise.all([
        vacationsAPI.getAll({ mine: true }),
        canManageOthers ? employeesAPI.getAll({ is_active: true }) : Promise.resolve({ data: [] }),
        feiertageAPI.getFlat(year),
        feiertageAPI.getCompanyFreeDays(year),
        schulferienAPI.getAll(year),
      ]);
      setVacations(vacRes.data);
      setEmployees(empRes.data || []);
      setHolidays(holRes.data || []);
      setCompanyFreeDays(freeDaysRes.data || []);
      setSchulferien(ferienRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [currentDate, canManageOthers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch preview when range changes
  useEffect(() => {
    const fetchPreview = async () => {
      if (!selectedRange.from || !selectedRange.to) {
        setPreview(null);
        return;
      }
      try {
        const startDate = format(selectedRange.from, 'yyyy-MM-dd');
        const endDate = format(selectedRange.to, 'yyyy-MM-dd');
        const empId = formData.employee_id || null;
        const res = await vacationsAPI.preview(startDate, endDate, empId);
        setPreview(res.data);
      } catch {
        setPreview(null);
      }
    };
    fetchPreview();
  }, [selectedRange.from, selectedRange.to, formData.employee_id]);

  const loadVacations = async () => {
    try {
      const response = await vacationsAPI.getAll({ mine: true });
      setVacations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Urlaubsdaten');
    }
  };

  const handleSubmit = async () => {
    if (!selectedRange.from || !selectedRange.to) {
      toast.error('Bitte Start- und Enddatum auswählen');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        start_date: format(selectedRange.from, 'yyyy-MM-dd'),
        end_date: format(selectedRange.to, 'yyyy-MM-dd'),
        vacation_type: formData.vacation_type,
        notes: formData.notes,
      };
      if (canManageOthers && formData.employee_id) {
        payload.employee_id = formData.employee_id;
      }
      await vacationsAPI.create(payload);
      toast.success(formData.employee_id ? 'Urlaub für Mitarbeiter eingetragen' : 'Urlaubsantrag eingereicht');
      setDialogOpen(false);
      setSelectedRange({ from: null, to: null });
      setFormData({ vacation_type: 'Urlaub', notes: '', employee_id: '' });
      loadVacations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Antrags');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Urlaubsantrag wirklich löschen/stornieren?')) return;
    try {
      await vacationsAPI.delete(id);
      toast.success('Urlaubsantrag gelöscht');
      loadVacations();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Navigation
  const navigatePrev = () => {
    switch (viewMode) {
      case 'day': setCurrentDate(subDays(currentDate, 1)); break;
      case 'week': setCurrentDate(subWeeks(currentDate, 1)); break;
      case 'month': setCurrentDate(subMonths(currentDate, 1)); break;
      case 'quarter': setCurrentDate(subMonths(currentDate, 3)); break;
    }
  };
  const navigateNext = () => {
    switch (viewMode) {
      case 'day': setCurrentDate(addDays(currentDate, 1)); break;
      case 'week': setCurrentDate(addWeeks(currentDate, 1)); break;
      case 'month': setCurrentDate(addMonths(currentDate, 1)); break;
      case 'quarter': setCurrentDate(addMonths(currentDate, 3)); break;
    }
  };
  const goToToday = () => setCurrentDate(new Date());

  // Drag
  const handleDragStart = (day) => {
    // Rückwirkende Urlaubseinträge sind erlaubt (z.B. Nacherfassung).
    setIsDragging(true);
    setDragStart(day);
    setDragEnd(day);
  };
  const handleDragEnter = (day) => {
    if (!isDragging) return;
    setDragEnd(day);
  };
  const handleDragFinish = useCallback(() => {
    if (!isDragging || !dragStart || !dragEnd) {
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    const from = dragStart < dragEnd ? dragStart : dragEnd;
    const to = dragStart < dragEnd ? dragEnd : dragStart;
    setSelectedRange({ from, to });
    setDialogOpen(true);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, dragStart, dragEnd]);

  const isDayInDragRange = (day) => {
    if (!isDragging || !dragStart || !dragEnd) return false;
    const from = dragStart < dragEnd ? dragStart : dragEnd;
    const to = dragStart < dragEnd ? dragEnd : dragStart;
    return day >= from && day <= to;
  };

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDragging) handleDragFinish();
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [isDragging, handleDragFinish]);

  // Helpers
  const getHolidayForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const holiday = holidays.find(h => h.date === dateStr);
    if (holiday) return holiday;
    const freeDay = companyFreeDays.find(h => h.date === dateStr);
    return freeDay || null;
  };

  const getSchulferienForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return schulferien.filter(f => dateStr >= f.start_date && dateStr <= f.end_date);
  };

  const getVacationsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return vacations.filter(v => dateStr >= v.start_date && dateStr <= v.end_date && v.status !== 'cancelled');
  };

  return {
    // state
    vacations, employees, loading, currentDate, setCurrentDate,
    viewMode, setViewMode, dialogOpen, setDialogOpen, saving,
    selectedRange, setSelectedRange, formData, setFormData, preview,
    // raw data (for dialog calendar markers)
    holidays, companyFreeDays, schulferien,
    // derived
    getHolidayForDay, getSchulferienForDay, getVacationsForDay,
    isDayInDragRange,
    // navigation
    navigatePrev, navigateNext, goToToday,
    // drag
    handleDragStart, handleDragEnter,
    // actions
    handleSubmit, handleDelete,
  };
}
